<template>
  <a-button
    :disabled="disabled || !hasRequiredObjects || alreadyCreated || completed"
    :loading="loading"
    @click="preview">
    {{ alreadyCreated || completed ? 'Проверки уже созданы' : 'Создать проверки планировки' }}
  </a-button>
  <a-modal
    v-model:open="visible"
    title="Проверки из настроек объектов"
    :width="800"
    centered
    :body-style="{ maxHeight: '72vh', overflowY: 'auto' }"
    :footer="null">
    <template v-if="created.length">
      <a-alert
        type="success"
        show-icon
        :message="`Создано проверок: ${created.length}`" />
      <div
        v-for="guard in created"
        :key="guard.id"
        class="rule-row">
        <router-link :to="`/guards/${guard.id}`">
          {{ guard.title }}
        </router-link>
      </div>
      <div class="actions">
        <a-button
          type="primary"
          @click="visible = false">
          Закрыть
        </a-button>
      </div>
    </template>
    <template v-else>
      <p>{{ source }}</p>
      <a-alert
        type="info"
        show-icon
        message="Для каждой версии проверки создаются один раз."
        :description="description" />
      <a-empty
        v-if="!rules.length"
        description="Нет настроек объектов" />
      <a-form-item
        label="Общий уровень замечаний"
        class="severity">
        <a-select
          v-model:value="severity"
          :options="severityOptions" />
      </a-form-item>
      <div
        v-for="(rule, index) in rules"
        :key="index"
        class="rule-row">
        <img
          v-if="rule.asset.preview_url"
          :src="rule.asset.preview_url"
          loading="lazy"
          alt="">
        <div class="rule-title">
          <strong>{{ rule.title }}</strong><div>{{ rule.asset.name }}</div>
        </div>
        <a-select
          v-model:value="ruleSeverities[index]"
          :options="individualSeverityOptions"
          class="rule-severity" />
      </div>
      <div class="actions">
        <a-button @click="visible = false">
          Отменить
        </a-button>
        <a-button
          type="primary"
          :loading="creating"
          :disabled="creating || !rules.length"
          @click="create">
          Создать проверки ({{ rules.length }})
        </a-button>
      </div>
    </template>
  </a-modal>
</template>

<script setup>
import { computed, ref, watch } from 'vue';
import { message } from 'ant-design-vue';
import { httpClient } from '@/httpClient';

const props = defineProps({
  endpoint: { type: String, required: true },
  sourceId: { type: Number, default: null },
  alreadyCreated: { type: Boolean, default: false },
  disabled: { type: Boolean, default: false },
  hasRequiredObjects: { type: Boolean, default: true },
});
const description = 'Новый набор заменит предыдущий набор этой сущности. '
  + 'Старые проверки без других привязок будут удалены. '
  + 'Мебель проверяется как обязательный объект в комнате.';
const emit = defineEmits(['created']);
const completed = ref(false);
watch(() => props.sourceId, () => { completed.value = false; });
const visible = ref(false);
const loading = ref(false);
const creating = ref(false);
const rules = ref([]);
const source = ref('');
const previewSourceId = ref(null);
const created = ref([]);
const severity = ref('warning');
const ruleSeverities = ref({});
const severityOptions = [
  { value: 'info', label: 'Информация' },
  { value: 'warning', label: 'Предупреждение' },
  { value: 'error', label: 'Ошибка' },
];
const individualSeverityOptions = computed(() => [
  { value: 'default', label: `Общий: ${severityOptions.find((item) => item.value === severity.value)?.label}` },
  ...severityOptions,
]);
const preview = async () => {
  loading.value = true;
  try {
    const { data } = await httpClient.get(`${props.endpoint}/preview`, {
      params: props.sourceId ? { source_id: props.sourceId } : {},
    });
    if (data.guards_created_at) {
      completed.value = true;
      message.info('Проверки для этой версии уже созданы');
      return;
    }
    rules.value = data.rules;
    source.value = data.source;
    previewSourceId.value = data.source_id;
    created.value = [];
    severity.value = 'warning';
    ruleSeverities.value = Object.fromEntries(data.rules.map((_, index) => [index, 'default']));
    visible.value = true;
  } catch (error) {
    message.error(error.response?.data?.message || 'Не удалось подготовить проверки');
  } finally {
    loading.value = false;
  }
};
const create = async () => {
  creating.value = true;
  try {
    const { data } = await httpClient.post(props.endpoint, {
      source_id: previewSourceId.value,
      severity: severity.value,
      rule_severities: Object.fromEntries(
        Object.entries(ruleSeverities.value).filter(([, value]) => value !== 'default'),
      ),
    });
    created.value = data.guards;
    completed.value = true;
    emit('created');
  } catch (error) {
    message.error(error.response?.data?.message || 'Не удалось создать проверки');
  } finally {
    creating.value = false;
  }
};
</script>

<style scoped>
.rule-row { display: flex; align-items: center; gap: 12px; padding: 12px 0; border-bottom: 1px solid #f0f0f0; }
.rule-title { flex: 1; min-width: 0; }
.rule-severity { width: 220px; flex: none; }
.rule-row img { width: 52px; height: 52px; object-fit: contain; }
.rule-row div div { color: #8c8c8c; margin-top: 3px; }
.severity { margin-top: 20px; max-width: 330px; }
.actions {
  position: sticky; bottom: 0; background: white; padding: 12px 0;
  display: flex; justify-content: flex-end; gap: 8px; margin-top: 20px; }
</style>
